import * as React from 'react';
import { NotificationsView, NotificationsViewProps } from './Notifications.view';
import { useContext, useEffect, useState } from 'react';
import { BulkMarkNotificationsAsReadMutationFn, MarkAllNotificationsAsReadMutationFn } from '../../../models/graphql';
import { triggerErrorToast } from '../../../helpers/triggerErrorToast';
import { getFirstScrollableElement } from '../../../helpers/scrollableHelpers';
import { ApolloError } from '@apollo/client';
import { AppContext } from '../../../providers/AppProvider';

export type NotificationsControllerProps = Omit<
  NotificationsViewProps,
  'paginate' | 'paginationStatus' | 'selectedWorkspaceId'
> & {
  markAllAsRead: MarkAllNotificationsAsReadMutationFn;
  markAsRead: BulkMarkNotificationsAsReadMutationFn;
};

export type NotificationsPaginationStatus = {
  isFetching: boolean;
  error: ApolloError | false;
  allLoaded: boolean;
  offset: number;
};

export const paginateLimit = 25;

export const NotificationsController = (props: NotificationsControllerProps): JSX.Element => {
  // const navigate = useNavigate();
  const { selectedWorkspaceId } = useContext(AppContext);
  const [paginationStatus, setPaginationStatus] = useState<NotificationsPaginationStatus>({
    isFetching: false,
    error: false,
    allLoaded:
      !!props.notificationsResult.data &&
      props.notificationsResult.data.notifications.items.length === props.notificationsResult.data.notifications.count,
    offset: 0
  });

  // Update offset count + all loaded when we get data back.
  // Set the unread count badge to 0 when we receive notifications.
  useEffect(() => {
    if (props.notificationsResult.data) {
      const notifications = props.notificationsResult.data.notifications;

      setPaginationStatus(status => {
        let allLoaded = status.allLoaded;
        if (notifications.items.length === notifications.count) {
          allLoaded = true;
        }
        return { ...status, offset: notifications.items.length, allLoaded };
      });
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [props.notificationsResult.data]);

  useEffect(() => {
    if (props.notificationsResult.error) {
      triggerErrorToast('Notification Error')(props.notificationsResult.error);
    }
  }, [props.notificationsResult.error]);

  const paginate = (e: React.UIEvent) => {
    const scrollableElement = getFirstScrollableElement(e.currentTarget as HTMLElement);
    const isNearBottom = scrollableElement ? scrollableElement?.scrollTop >= scrollableElement?.offsetHeight : false;

    if (
      props.notificationsResult.data &&
      !paginationStatus.isFetching &&
      !paginationStatus.error &&
      !paginationStatus.allLoaded &&
      isNearBottom
    ) {
      setPaginationStatus(status => {
        return { ...status, isFetching: true };
      });
      props.notificationsResult
        .fetchMore({
          variables: { orgId: props.selectedOrganizationId, offset: paginationStatus.offset, limit: paginateLimit }
        })
        .then(result => {
          setPaginationStatus(status => {
            // Set all loaded to true if we received no items.
            return { ...status, allLoaded: result.data.notifications.items.length === 0 };
          });
        })
        .catch(e => {
          setPaginationStatus(status => {
            return { ...status, error: e };
          });
        })
        .finally(() => {
          setPaginationStatus(status => {
            return { ...status, isFetching: false };
          });
        });
    }
  };

  const viewProps = { ...props, paginate, paginationStatus, selectedWorkspaceId };
  return <NotificationsView {...viewProps} />;
};
